import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import type { PlaceSuggestion } from '@mr-website/schemas';
import { Loader2, MapPin } from 'lucide-react';
import { useRef } from 'react';

export type { PlaceSuggestion };

interface AddressAutocompleteProps {
  id?: string;
  value: string;
  inputValue?: string;
  onInputChange: (input: string) => void;
  suggestions: PlaceSuggestion[];
  isLoading: boolean;
  onSelect: (placeId: string, description: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
}

// Base UI Combobox auto-uses `.label` for display and `.value` for form submission
// when item values are shaped as { value, label }
type SuggestionOption = { value: string; label: string };

export function AddressAutocomplete({
  id,
  value,
  inputValue,
  onInputChange,
  suggestions,
  isLoading,
  onSelect,
  onBlur,
  placeholder,
  disabled,
}: AddressAutocompleteProps) {
  // Suppress onInputValueChange while a selection is resolving.
  // Base UI fires it multiple times after selection (with the label, then with "")
  // because value={null} causes it to reset the input. We hold the flag until
  // the resolved value arrives via the controlled inputValue prop.
  const selectingRef = useRef(false);

  // Track whether the dropdown popup is open so we can distinguish Base UI's
  // close-time reset (reason: "input-clear", popup closing) from the user
  // backspacing the last character (same reason, but popup is still open).
  const popupOpenRef = useRef(false);

  const resolvedInputValue = inputValue ?? value;

  // Track the last value we intentionally propagated so we can restore it when
  // Base UI fires its close-time reset (reason: "input-clear" from handleUnmount).
  // Updated on every render so it stays in sync with parent-controlled updates
  // (e.g., after Google Places resolution writes the parsed street back).
  const lastKnownValueRef = useRef(resolvedInputValue);
  lastKnownValueRef.current = resolvedInputValue;

  const options: SuggestionOption[] = suggestions.map((s) => ({
    value: s.placeId,
    label: s.description,
  }));

  return (
    <Combobox<SuggestionOption>
      // Disable built-in filtering — Google already filters results server-side
      filter={null}
      inputValue={resolvedInputValue}
      value={null}
      onOpenChange={(open) => {
        popupOpenRef.current = open;
      }}
      onValueChange={(option) => {
        if (option) {
          selectingRef.current = true;
          onSelect(option.value, option.label);
        }
      }}
      onInputValueChange={(newValue, eventDetails) => {
        if (selectingRef.current) {
          if (newValue !== '') {
            // First non-empty call is the label — suppress it, stay in selecting mode
            // until Base UI's trailing "" reset fires
            return;
          }
          // Base UI's trailing "" reset — suppress it and exit selecting mode
          selectingRef.current = false;
          return;
        }
        // Base UI fires onInputValueChange("", { reason: "input-clear" }) via
        // handleUnmount when the popup closes, because value={null} stringifies
        // to "". This is not a user action — suppress it and restore the last
        // known value so free-text entry is preserved when tabbing away without
        // selecting a suggestion. True user clears use reason "input-change"
        // (backspace) or "clear-press" (X button) and are allowed through.
        if (newValue === '' && eventDetails.reason === 'input-clear' && !popupOpenRef.current) {
          onInputChange(lastKnownValueRef.current);
          return;
        }
        onInputChange(newValue);
      }}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        onBlur={onBlur}
        showTrigger={false}
        showClear
        autoComplete="off"
        className="w-full"
      />
      <ComboboxContent>
        <ComboboxList>
          {isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {options.length === 0 && <ComboboxEmpty>No addresses found.</ComboboxEmpty>}
              {options.map((opt) => (
                <ComboboxItem key={opt.value} value={opt}>
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{opt.label}</span>
                </ComboboxItem>
              ))}
            </>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
